import React from 'react';
import { Link } from 'react-router-dom';
import homeImg from '../assets/home.jpg';
import marriageVideo from '../assets/marriage.mp4';

const Home = () => {
  return (
    <div className="space-y-16 pb-12">
      {/* HERO SECTION */}
      <section className="relative bg-[#2C1A14] text-white py-20 px-4 sm:px-6 lg:px-8 border-b border-[#D4B896]/20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
          
          {/* Left Column: Headline & CTA */}
          <div className="w-full md:w-1/2 text-left space-y-6">
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D4B896] bg-[#D4B896]/10 px-3 py-1 rounded-full border border-[#D4B896]/30 inline-block">
              Live Life with Gratitude and Humility
            </span>
            <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl font-bold text-white tracking-wide leading-tight">
              Welcome to the <span className="text-[#D4B896]">MBA Trust</span>
            </h1>
            <p className="text-stone-300 text-sm sm:text-base leading-relaxed max-w-xl">
              Dedicated to uniting our community, maintaining our family legacy, and supporting future generations through trust, welfare initiatives, and shared development.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="/donate"
                className="px-6 py-3 bg-[#D4B896] hover:bg-white text-[#2C1A14] font-bold text-xs uppercase tracking-widest rounded-xl shadow-md transition-all duration-300"
              >
                Support the Trust
              </Link>
              <Link
                to="/events"
                className="px-6 py-3 bg-transparent hover:bg-white/10 text-stone-200 border border-[#D4B896]/40 font-semibold text-xs uppercase tracking-widest rounded-xl transition-all duration-300"
              >
                Upcoming Events
              </Link>
            </div>
          </div>

          {/* Right Column: Hero Visual Showcase */}
          <div className="w-full md:w-1/2 flex justify-center">
            <div className="relative w-full max-w-md bg-[#1a0f0c] p-4 rounded-3xl border border-[#D4B896]/30 shadow-2xl">
              <img
                src={homeImg}
                alt="MBA Trust Overview"
                className="w-full h-80 sm:h-96 object-cover rounded-2xl shadow-inner"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#D4B896] text-[#2C1A14] p-4 rounded-2xl shadow-lg border border-white/20 hidden sm:block">
                <p className="font-serif font-bold text-lg leading-tight">Established Legacy</p>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#2C1A14]/80">Building for tomorrow</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* CORE OBJECTIVES / ABOUT SUMMARY SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center space-y-3 mb-12">
          <h2 className="font-serif text-2xl sm:text-4xl font-bold text-[#2C1A14]">
            Pillars of Our Trust
          </h2>
          <p className="text-stone-600 text-sm max-w-2xl mx-auto">
            Our commitment rests on three central values that ensure our legacy remains strong and supportive.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Card 1 */}
          <div className="bg-[#2C1A14]/5 p-8 rounded-2xl border border-[#D4B896]/40 hover:border-[#D4B896] transition-all duration-300 space-y-4">
            <div className="w-12 h-12 bg-[#2C1A14] text-[#D4B896] rounded-xl flex items-center justify-center font-serif text-xl font-bold">
              01
            </div>
            <h3 className="font-serif text-xl font-bold text-[#2C1A14]">Heritage & Unity</h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Fostering connection and preserving common lineage traditions across generations.
            </p>
          </div>

          {/* Card 2 */}
          <div className="bg-[#2C1A14]/5 p-8 rounded-2xl border border-[#D4B896]/40 hover:border-[#D4B896] transition-all duration-300 space-y-4">
            <div className="w-12 h-12 bg-[#2C1A14] text-[#D4B896] rounded-xl flex items-center justify-center font-serif text-xl font-bold">
              02
            </div>
            <h3 className="font-serif text-xl font-bold text-[#2C1A14]">Welfare & Support</h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Providing assistance, educational resources, and developmental aid to members in need.
            </p>
          </div>

          {/* Card 3 */}
          <div className="bg-[#2C1A14]/5 p-8 rounded-2xl border border-[#D4B896]/40 hover:border-[#D4B896] transition-all duration-300 space-y-4">
            <div className="w-12 h-12 bg-[#2C1A14] text-[#D4B896] rounded-xl flex items-center justify-center font-serif text-xl font-bold">
              03
            </div>
            <h3 className="font-serif text-xl font-bold text-[#2C1A14]">Future Growth</h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Creating sustainable initiatives and digital platforms that empower generations to come.
            </p>
          </div>
        </div>
      </section>

      {/* REPLACED FAMILY CHARTER SECTION (WITH UNMUTED/CONTROLLABLE MARRIAGE.MP4 VIDEO) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-[#2C1A14] rounded-3xl p-6 sm:p-10 border border-[#D4B896]/30 shadow-xl overflow-hidden">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            
            {/* The marriage.mp4 Video Container */}
            <div className="w-full lg:w-1/2 overflow-hidden rounded-2xl border border-[#D4B896]/20 shadow-md">
              <video
                src={marriageVideo}
                controls
                autoPlay
                loop
                playsInline
                className="w-full h-64 sm:h-80 md:h-96 object-cover"
              />
            </div>

            {/* Accompanying Content Block */}
            <div className="w-full lg:w-1/2 space-y-4 text-left">
              <span className="text-xs uppercase tracking-widest text-[#D4B896] font-semibold">
                Our Foundation
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white">
                Guiding Principles & Legacy
              </h2>
              <p className="text-stone-300 text-sm sm:text-base leading-relaxed">
                Our foundation is built upon long-standing trust and commitment. Through unified effort and structured governance, we continuously aim to enrich family and community life to safeguard our shared history.
              </p>
              <div className="pt-2">
                <Link
                  to="/work"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-[#D4B896] hover:text-white transition-colors"
                >
                  <span>Explore Our Initiatives</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CLEAN CONTACT BAR */}
      <footer className="border-t border-[#D4B896]/40 pt-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-base sm:text-lg font-sans text-stone-700">
            <span className="font-semibold text-[#2C1A14]">Contact:</span>{' '}
            <a
              href="mailto:officialmbatrust@gmail.com"
              className="font-bold text-[#2C1A14] hover:text-[#D4B896] transition-colors underline underline-offset-4"
            >
              officialmbatrust@gmail.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;